import * as React from 'react'
import { Check, Loader2 } from 'lucide-react'
import type { OrganizationDto } from '@lib/dto/organization.dto'
import { useUpdateOrganization } from '@/hooks/useOrganization'
import { Button } from '@/components/ui/button'
import { errorMessage } from '@/helpers/logger'

function Rule({
  id,
  label,
  help,
  checked,
  onChange,
}: {
  id: string
  label: string
  help: React.ReactNode
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex gap-2.5">
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0"
        checked={checked}
        onChange={(event) => {
          onChange(event.target.checked)
        }}
      />
      <label htmlFor={id} className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        <span className="mt-0.5 block text-xs text-muted-text">{help}</span>
      </label>
    </div>
  )
}

/**
 * The two rules that decide how a proposal is priced and what a client may
 * change about it.
 *
 * They belong together because they lean on each other: while itemizing is
 * optional, a client's pruning toggle only reaches the trees that were split,
 * so the second rule is what makes the first one clean.
 */
export function ProposalRulesSettings({
  organization,
}: {
  organization: OrganizationDto
}) {
  const updateOrganization = useUpdateOrganization()
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)

  const [draft, setDraft] = React.useState({
    clientsMaySelectJobTypes: organization.clientsMaySelectJobTypes,
    requireItemizedEstimates: organization.requireItemizedEstimates,
  })

  const isDirty =
    draft.clientsMaySelectJobTypes !== organization.clientsMaySelectJobTypes ||
    draft.requireItemizedEstimates !== organization.requireItemizedEstimates

  async function handleSave(): Promise<void> {
    setError(null)
    setSaved(false)
    try {
      await updateOrganization.mutateAsync(draft)
      setSaved(true)
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div>
        <h2 className="text-lg font-semibold">Proposal rules</h2>
        <p className="mt-0.5 text-sm text-muted-text">
          How work is priced, and how much of it a client can change.
        </p>
      </div>

      <div className="space-y-3">
        <Rule
          id="require-itemized"
          label="Require a time for every pruning job"
          help="A tree carrying pruning jobs cannot be saved until its time is split across them. Without this, a tree keeps one overall estimate."
          checked={draft.requireItemizedEstimates}
          onChange={(next) => {
            setSaved(false)
            setDraft({ ...draft, requireItemizedEstimates: next })
          }}
        />
        <Rule
          id="clients-select-jobs"
          label="Let clients decline individual pruning job types"
          help={
            <>
              On the proposal they open, a client can untick a kind of pruning
              and the total follows. They can always decline a problem, a
              site or a species, and can never decline access and clean-up.
              {draft.clientsMaySelectJobTypes &&
              !draft.requireItemizedEstimates ? (
                <span className="mt-1 block text-warning">
                  Without the rule above, this only reaches trees whose time
                  was split — so declining a kind of pruning reduces some trees
                  and not others.
                </span>
              ) : null}
            </>
          }
          checked={draft.clientsMaySelectJobTypes}
          onChange={(next) => {
            setSaved(false)
            setDraft({ ...draft, clientsMaySelectJobTypes: next })
          }}
        />
      </div>

      {/*
        Said once, because it is the surprising half: everything else in a sent
        proposal is frozen, and somebody turning this off would reasonably
        wonder whether they had just changed an offer a client is reading.
      */}
      <p className="rounded-md border border-default bg-subtle px-3 py-2 text-xs text-secondary-text">
        What a client may choose is part of the offer they were sent. Changing
        it applies to proposals sent from here on; one already with a client
        keeps the terms it went out with.
      </p>

      {error === null ? null : <p className="text-sm text-danger">{error}</p>}

      <div className="flex items-center gap-3 border-t border-default pt-3">
        <Button
          disabled={!isDirty || updateOrganization.isPending}
          onClick={() => {
            void handleSave()
          }}
        >
          {updateOrganization.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : null}
          Save changes
        </Button>
        {saved && !isDirty ? (
          <span className="flex items-center gap-1 text-sm text-muted-text">
            <Check className="h-3.5 w-3.5" />
            Saved
          </span>
        ) : null}
      </div>
    </section>
  )
}
